// @spec NIS2-STR-038
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { AuditorNIS2MeasureDetail } from '@/features/auditor-access/components/AuditorNIS2MeasureDetail';
import { AuditorNIS2Measures } from '@/features/auditor-access/components/AuditorNIS2Measures';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/nis2/measures')({
  component: AuditorNIS2MeasuresPage,
});

/**
 * Auditor NIS2 measures page.
 *
 * Displays a read-only list of Article 21 measures with option to view detail.
 */
function AuditorNIS2MeasuresPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const [selectedMeasureCode, setSelectedMeasureCode] = useState<string | null>(null);

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'nis2_compliance'
  if (!currentAccess.accessScope.includes('nis2_compliance')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              Your auditor access does not include permission to view NIS2 compliance data.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const handleMeasureClick = (measureCode: string) => {
    setSelectedMeasureCode(measureCode);
  };

  const handleBackToList = () => {
    setSelectedMeasureCode(null);
  };

  const handleBackToOverview = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2` });
  };

  return (
    <div className="min-h-screen bg-background">
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          {selectedMeasureCode ? (
            <AuditorNIS2MeasureDetail
              accessId={accessId}
              measureCode={selectedMeasureCode}
              onBack={handleBackToList}
            />
          ) : (
            <AuditorNIS2Measures
              accessId={accessId}
              organizationName={currentAccess.name}
              onMeasureClick={handleMeasureClick}
              onBackToOverview={handleBackToOverview}
            />
          )}
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
